import { useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "@phosphor-icons/react";
import { useModalFocus } from "./react.tsx";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  size?: "sm" | "lg" | "xl";
  children: ReactNode;
}

const MODAL_SIZE_CLASSES: Record<NonNullable<ModalProps["size"]>, string> = {
  sm: "w-[360px]",
  lg: "w-[480px]",
  xl: "h-[80vh] w-[80vw]"
};

export function Modal({ open, onClose, title, size = "sm", children }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useModalFocus(open, dialogRef, onClose);

  if (!open) return null;
  return createPortal(
    <div
      data-modal-layer=""
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
    >
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        className="absolute inset-0 cursor-default bg-black/45"
        onClick={onClose}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`relative flex flex-col gap-4 rounded-xl border border-kumo-line bg-kumo-base p-6 shadow-lg ${MODAL_SIZE_CLASSES[size]} max-w-[calc(100vw-2rem)]`}
      >
        <div className="flex items-center justify-between">
          <div
            id={titleId}
            className="flex items-center gap-2 text-[15px] font-semibold text-kumo-default"
          >
            {title}
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md border border-kumo-line bg-kumo-base text-kumo-default"
          >
            <X size={14} />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}
